/**
 * BaseAdSetForm — shared shell for all per-objective ad set forms.
 * Handles: name, billing event, optimization goal, budget/schedule, targeting, save.
 * Per-objective fields are injected via `children` (extra fields rendered between name and targeting).
 */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Lock, AlertTriangle, ExternalLink } from 'lucide-react';
import { useFbWorkspace } from '@/contexts/FbWorkspaceContext';
import { facebookAdSetsService } from '@/services/facebook-ad-sets.service';
import { facebookCampaignsService } from '@/services/facebook-campaigns.service';
import { useMetaAccountCurrency } from '@/hooks/useMetaAccountCurrency';
import { CurrencyTip } from '@/components/ads/BillingCurrency';
import { COST_CAP_REFUSED_GOALS, billingEventsForGoal, goalNotice } from '@/lib/meta-matrix';
import { ensureCampaignSaved } from '../saveHelpers';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { DateTimePicker } from '@/components/DateTimePicker';
import { TargetingSection, TargetingValue, defaultTargeting, buildTargetingPayload } from '../targeting/TargetingSection';
import { PlacementPicker, PlacementValue, defaultPlacement, applyPlacementToTargeting } from '@/components/facebook/MetaResourcePickers';

// ─── Segmented helper ─────────────────────────────────────────────────────────

interface SegProps { options: { v: string; l: string; icon?: React.ReactNode }[]; value: string; onChange: (v: string) => void; disabled?: boolean; }
export function Seg({ options, value, onChange, disabled }: SegProps) {
  return (
    <div className="inline-flex flex-wrap rounded-lg border border-border bg-surface p-0.5 gap-0.5">
      {options.map(o => (
        <button
          key={o.v}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.v)}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all disabled:opacity-60 ${o.v === value ? 'bg-primary text-on-primary shadow-soft' : 'text-text-muted hover:bg-surface-alt hover:text-text-primary'}`}
        >
          {o.icon}
          {o.l}
        </button>
      ))}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdSetBaseFormData {
  name: string;
  optimization_goal: string;
  billing_event: string;
  bid_amount: string;
  daily_budget: string;
  lifetime_budget: string;
  budget_kind: 'daily' | 'lifetime';
  start_time: string;
  end_time: string;
  targeting: TargetingValue;
  placement?: PlacementValue;
  promoted_object?: Record<string, string>;
  destination_type?: string;
  attribution_spec?: Array<{ event_type: string; window_days: number }>;
}

export interface BaseAdSetFormProps {
  optimizationGoals: { code: string; label: string }[];
  billingEvents?: { code: string; label: string }[];
  extraPayload?: () => Record<string, unknown>;
  /**
   * Returns extra fields to merge into the `targeting` object (one level deep)
   * before send. Used by App Promotion forms to inject `user_os` so Meta's
   * "app OS vs targeting OS" validator passes.
   */
  extraTargeting?: () => Record<string, unknown> | undefined;
  /** Return null when valid, or an error string when not — blocks save with a toast. */
  validateExtra?: () => string | null;
  /**
   * Notifies the per-objective form of the current placement selection so it
   * can suppress `instagram_actor_id` when 'instagram' is excluded.
   */
  onPlacementChange?: (p: PlacementValue) => void;
  /**
   * Notifies the per-objective form when the optimization goal changes. Used
   * by EngagementAdSetForm so it can show/hide the conversation-destination
   * sub-fields only when goal === 'CONVERSATIONS'.
   */
  onOptimizationGoalChange?: (goal: string) => void;
  /**
   * The objective this form is built for (e.g. 'OUTCOME_ENGAGEMENT'). When
   * set, BaseAdSetForm fetches the campaign from Meta on mount and surfaces
   * a warning banner if the campaign's actual objective doesn't match — so
   * the user finds out BEFORE save instead of seeing Meta's confusing
   * "performance goal not compatible" rejection. Optional; existing forms
   * default to no check.
   */
  expectedObjective?: string;
  /** Conversion location, for notices that only apply to one location (e.g. Sales → App conversions). */
  noticeLocation?: string;
  children?: React.ReactNode;
}

// Billing events are derived from the selected optimization goal via
// billingEventsForGoal() — Meta validates the (goal, billing) pair atomically
// and the old static list here offered pairings Meta rejects with a cryptic
// error. A `billingEvents` prop, when passed, still overrides.

// ─── BaseAdSetForm ────────────────────────────────────────────────────────────

export const BaseAdSetForm: React.FC<BaseAdSetFormProps> = ({
  optimizationGoals,
  billingEvents,
  extraPayload,
  extraTargeting,
  validateExtra,
  onPlacementChange,
  onOptimizationGoalChange,
  expectedObjective,
  noticeLocation,
  children,
}) => {
  const {
    activeAdSetId, activeAdSet, campaign, isSubmitting, setIsSubmitting,
    updateAdSetForm, lockAdSet, unlockAdSet, lockCampaign, platform, appPromotion, addAd, setActiveLevel,
  } = useFbWorkspace();
  // The only way to the first ad used to be the ad set's hidden "…" menu in the sidebar.
  const goToAd = () => {
    if (!activeAdSetId) return;
    const firstAd = activeAdSet?.ads[0];
    if (firstAd) setActiveLevel('ad', activeAdSetId, firstAd.id);
    else addAd(activeAdSetId);
  };
  const isLocked = activeAdSet?.isLocked ?? false;
  const { currency: defaultCurrency } = useMetaAccountCurrency();
  const currency = appPromotion?.accountCurrency ?? defaultCurrency;

  // ── Objective drift detection ───────────────────────────────────────────
  // Fetch the campaign's actual objective from Meta. If the workspace's
  // local draft says one objective but Meta has another (common when the
  // user edits a campaign in Ads Manager directly, or restores a stale
  // draft), surface that BEFORE save so the user understands why goals
  // would be rejected.
  const [actualObjective, setActualObjective] = useState<string | null>(null);
  const [objectiveCheckDone, setObjectiveCheckDone] = useState(false);
  useEffect(() => {
    if (!campaign.apiId || !expectedObjective) {
      setObjectiveCheckDone(true);
      return;
    }
    let cancelled = false;
    facebookCampaignsService.get(campaign.apiId)
      .then((c) => {
        if (cancelled) return;
        setActualObjective(c.objective ?? null);
        setObjectiveCheckDone(true);
      })
      .catch(() => { if (!cancelled) setObjectiveCheckDone(true); });
    return () => { cancelled = true; };
  }, [campaign.apiId, expectedObjective]);

  const objectiveDrift = !!(
    objectiveCheckDone
    && expectedObjective
    && actualObjective
    && actualObjective !== expectedObjective
  );

  const fd = (activeAdSet?.formData ?? {}) as Record<string, unknown>;

  // Local state. KEY: re-initialise whenever `activeAdSetId` changes, so
  // switching between ad sets does NOT bleed values from one into the other.
  const [name, setName]         = useState<string>((fd.name as string) ?? '');
  const [optGoal, setOptGoal]   = useState<string>((fd.optimization_goal as string) ?? (optimizationGoals[0]?.code ?? ''));
  const [billing, setBilling]   = useState<string>((fd.billing_event as string) ?? billingEvents?.[0]?.code ?? 'IMPRESSIONS');
  const [bidAmt, setBidAmt]     = useState<string>((fd.bid_amount as string) ?? '');
  const [budgetKind, setBudgetKind] = useState<'daily' | 'lifetime'>((fd.budget_kind as 'daily' | 'lifetime') ?? 'daily');
  const [budget, setBudget]     = useState<string>(
    (fd.budget_kind === 'lifetime' ? (fd.lifetime_budget as string) : (fd.daily_budget as string)) ?? '',
  );
  const [startTime, setStart]   = useState<string>((fd.start_time as string) ?? '');
  const [endTime, setEnd]       = useState<string>((fd.end_time as string) ?? '');
  const [targeting, setTargeting] = useState<TargetingValue>(
    (fd.targeting as TargetingValue | undefined) ?? defaultTargeting()
  );
  const [placement, setPlacement] = useState<PlacementValue>(
    (fd.placement as PlacementValue | undefined) ?? defaultPlacement()
  );

  // Billing options follow the selected goal; a `billingEvents` prop override
  // still wins for objective forms that need something bespoke.
  const goalBillingEvents = billingEvents ?? billingEventsForGoal(optGoal);

  // Reset all local state when the user switches to a different ad set. The
  // parent supplies `key={activeAdSetId}`, so this useEffect is mostly a
  // safety net for re-mount edge cases.
  useEffect(() => {
    const f = (activeAdSet?.formData ?? {}) as Record<string, unknown>;
    setName((f.name as string) ?? '');
    const restoredGoal = (f.optimization_goal as string) ?? (optimizationGoals[0]?.code ?? '');
    setOptGoal(restoredGoal);
    // Clamp drafts saved before billing became goal-dependent — a stale
    // pairing (e.g. POST_ENGAGEMENT billing) would be rejected by Meta.
    const restoredBilling = (f.billing_event as string) ?? billingEvents?.[0]?.code ?? 'IMPRESSIONS';
    const validBilling = (billingEvents ?? billingEventsForGoal(restoredGoal)).map(b => b.code);
    setBilling(validBilling.includes(restoredBilling) ? restoredBilling : 'IMPRESSIONS');
    setBidAmt((f.bid_amount as string) ?? '');
    setBudgetKind((f.budget_kind as 'daily' | 'lifetime') ?? 'daily');
    setBudget((f.budget_kind === 'lifetime' ? (f.lifetime_budget as string) : (f.daily_budget as string)) ?? '');
    setStart((f.start_time as string) ?? '');
    setEnd((f.end_time as string) ?? '');
    setTargeting((f.targeting as TargetingValue | undefined) ?? defaultTargeting());
    setPlacement((f.placement as PlacementValue | undefined) ?? defaultPlacement());
    onPlacementChange?.((f.placement as PlacementValue | undefined) ?? defaultPlacement());
    onOptimizationGoalChange?.((f.optimization_goal as string) ?? (optimizationGoals[0]?.code ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAdSetId]);

  const budgetAt = (campaign.formData as Record<string, unknown>).budget_at as string ?? 'campaign';
  const showBudget = budgetAt === 'adset';
  // A CBO campaign with a lifetime budget still needs each AD SET to end >24 h after it starts —
  // Meta refused every such ad set because this form only asked for an end date under ABO lifetime.
  const campaignFd = campaign.formData as Record<string, unknown>;
  const campaignLifetime = !showBudget && campaignFd.budget_kind === 'lifetime';
  const needsEnd = (showBudget && budgetKind === 'lifetime') || campaignLifetime;
  const campaignStop = (campaignFd.stop_time as string | undefined) ?? '';
  useEffect(() => {
    if (campaignLifetime && !endTime && campaignStop && !isLocked) { setEnd(campaignStop); sf('end_time', campaignStop); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignLifetime, campaignStop, activeAdSetId]);

  // Bid strategy is set on the campaign. The ad set's `bid_amount` is only
  // accepted by Meta when the strategy is one of the cap/target ones. Sending
  // bid_amount with LOWEST_COST_WITHOUT_CAP triggers:
  //   "You can't set a bid cap on ad sets with LOWEST_COST_WITHOUT_CAP bid
  //    strategy. Please either remove bid_amount or change to use
  //    LOWEST_COST_WITH_BID_CAP with bid_amount"
  // So gate the field by strategy and never send bid_amount otherwise.
  const campaignBidStrategy = (campaign.formData as Record<string, unknown>).bid_strategy as string | undefined;
  const showBidField =
    campaignBidStrategy === 'LOWEST_COST_WITH_BID_CAP' ||
    campaignBidStrategy === 'COST_CAP';
  const bidLabel =
    campaignBidStrategy === 'LOWEST_COST_WITH_BID_CAP' ? 'Bid cap' :
    campaignBidStrategy === 'COST_CAP'                 ? 'Cost cap' :
    'Bid cap / Cost cap';
  const bidHint =
    campaignBidStrategy === 'LOWEST_COST_WITH_BID_CAP' ? 'Maximum amount Meta will bid per impression.' :
    campaignBidStrategy === 'COST_CAP'                 ? 'Average cost per result Meta should aim to keep below.' :
    '';

  const sync = (updates: Record<string, unknown>) => {
    if (activeAdSetId) updateAdSetForm(activeAdSetId, updates);
  };
  const sf = (k: string, v: unknown) => { sync({ [k]: v }); };

  // Switching the conversion location swaps the goal list, but the picked goal stayed the old one — the select
  // then SHOWED the new location's goal while the old one was sent (Engagement On Post → On Page posted
  // POST_ENGAGEMENT and Meta refused it; UI E2E 2026-09-30). Snap to the first goal the location offers.
  const goalCodes = optimizationGoals.map(g => g.code).join(',');
  useEffect(() => {
    if (isLocked || optimizationGoals.length === 0 || optimizationGoals.some(g => g.code === optGoal)) return;
    const next = (optimizationGoals.find(g => !(campaignBidStrategy === 'COST_CAP' && COST_CAP_REFUSED_GOALS.includes(g.code))) ?? optimizationGoals[0]).code;
    setOptGoal(next); sf('optimization_goal', next); onOptimizationGoalChange?.(next);
    const valid = (billingEvents ?? billingEventsForGoal(next)).map(b => b.code);
    if (!valid.includes(billing)) { setBilling('IMPRESSIONS'); sf('billing_event', 'IMPRESSIONS'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalCodes, isLocked]);

  const handleSave = async () => {
    if (!activeAdSetId) return;
    if (!name) { toast.error('Ad set name is required'); return; }

    // Block save when we know the campaign on Meta doesn't match this form's
    // objective — Meta would reject and the toast would be confusing.
    if (objectiveDrift) {
      toast.error(`Wrong form: campaign is "${actualObjective}", not "${expectedObjective}". See the banner above.`, { duration: 6000 });
      return;
    }

    // Budget × schedule sanity. Meta produces opaque errors for these so we
    // catch them client-side with friendly messages.
    if (needsEnd && !endTime) {
      toast.error(campaignLifetime
        ? 'This campaign spends a lifetime budget, so the ad set needs an end time. Pick when it should stop.'
        : 'Lifetime budget needs an end time. Pick when the ad set should stop.');
      return;
    }
    if (needsEnd && endTime && new Date(endTime).getTime() - (startTime ? new Date(startTime).getTime() : Date.now()) < 24 * 3600_000) {
      toast.error('A lifetime-budget ad set must run for more than 24 hours — move the end time later.');
      return;
    }
    if (startTime && endTime && new Date(endTime).getTime() <= new Date(startTime).getTime()) {
      toast.error('End time must be after the start time.');
      return;
    }
    // When the campaign uses a cap-style bid strategy, the ad set MUST send a
    // bid_amount or Meta rejects it.
    if (showBidField && !bidAmt) {
      toast.error(`${bidLabel} is required because this campaign uses a manual bid strategy.`);
      return;
    }

    // ABO means THIS ad set carries the budget — an empty field used to go all
    // the way to Meta and come back with "daily budget must be greater than $0".
    if (showBudget && !budget) {
      toast.error(`Enter a ${budgetKind} budget for this ad set — the campaign doesn't carry one (ABO).`);
      return;
    }

    // Budget control defaults to CBO, so someone who never opens that section
    // gets a campaign with NO budget anywhere — and Meta rejects the ad set
    // with "daily budget must be greater than $0", pointing at the wrong level.
    if (!showBudget) {
      const cf = campaign.formData as Record<string, unknown>;
      const campaignBudget = cf.budget ?? cf.daily_budget ?? cf.lifetime_budget;
      if (!campaignBudget) {
        toast.error(
          'This campaign uses CBO but has no budget yet — set one under Campaign setup → Budget control, ' +
          'or switch it to "Ad Set (ABO)" and give this ad set a budget.',
        );
        return;
      }
    }

    if (campaignBidStrategy === 'COST_CAP' && COST_CAP_REFUSED_GOALS.includes(optGoal)) {
      toast.error('Meta doesn\'t allow this goal with a Cost cap bid strategy. Switch the campaign to "Highest volume" or "Bid cap", or pick another goal.');
      return;
    }

    if (validateExtra) {
      const err = validateExtra();
      if (err) { toast.error(err); return; }
    }

    setIsSubmitting(true);
    try {
      const campaignApiId = await ensureCampaignSaved(campaign, lockCampaign, appPromotion?.accountUid);
      if (!campaignApiId) { setIsSubmitting(false); return; }

      const targetingPayload = applyPlacementToTargeting(buildTargetingPayload(targeting), placement);
      if (extraTargeting) Object.assign(targetingPayload, extraTargeting());
      const base: Record<string, unknown> = {
        name,
        campaign_id: campaignApiId,
        optimization_goal: optGoal,
        billing_event: billing,
        status: 'PAUSED',
        targeting: targetingPayload,
      };
      if (showBidField && bidAmt) base.bid_amount = bidAmt;
      // ABO: Meta drops the strategy from a budget-less campaign, so the ad set must carry it — the
      // backend otherwise defaults to LOWEST_COST_WITHOUT_CAP and Meta refuses the bid_amount above.
      if (showBudget && campaignBidStrategy) base.bid_strategy = campaignBidStrategy;
      if (showBudget && budget) {
        const minor = String(Math.round(parseFloat(budget) * 100));
        if (budgetKind === 'daily') base.daily_budget = minor;
        else base.lifetime_budget = minor;
      }
      if (startTime) base.start_time = startTime;
      if (endTime) base.end_time = endTime;
      if (extraPayload) Object.assign(base, extraPayload());
      if (appPromotion) base.expected_account_uid = appPromotion.accountUid;

      const res = await facebookAdSetsService.create(base as unknown as Parameters<typeof facebookAdSetsService.create>[0]);
      // The ad form needs the conversion location (lead form or not, which CTAs fit); the objective forms keep it in local state.
      // The ad form reads these: the conversion location decides the CTAs; the WhatsApp number pre-fills the
      // WhatsApp CTA (it used to be typed a second time after picking it here).
      const promoted = (base.promoted_object ?? {}) as Record<string, unknown>;
      sync({ destination_type: (base.destination_type as string | undefined) ?? '', whatsapp_phone_number: (promoted.whatsapp_phone_number as string | undefined) ?? '' });
      lockAdSet(activeAdSetId, res.id, res.id);
      toast.success('Ad set saved');
      goToAd();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 w-full">
      {/* Objective-drift banner — fires when the campaign on Meta's side has
          a different objective than what this ad-set form is built for. */}
      {objectiveDrift && (
        <div className="flex items-start gap-3 rounded-2xl border border-danger/40 bg-danger-soft px-4 py-3">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-danger" />
          <div className="min-w-0 flex-1 text-xs leading-relaxed text-text-secondary">
            <div className="font-semibold text-danger mb-1">
              Wrong ad-set form for this campaign
            </div>
            <p>
              This form is built for <strong className="text-text-primary">{expectedObjective}</strong> campaigns,
              but the campaign on Meta is <strong className="text-text-primary">{actualObjective}</strong>.
              Saving from this form will be rejected by Meta because the optimisation goals shown here aren't compatible
              with <strong className="text-text-primary">{actualObjective}</strong>.
            </p>
            <p className="mt-1.5">
              <strong className="text-text-primary">Fix:</strong> The campaign objective is locked on Meta after creation
              — you can't change it via API. Either:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Discard this draft and create a new campaign with objective = <strong className="text-text-primary">{expectedObjective}</strong>, OR</li>
              <li>Use the form for <strong className="text-text-primary">{actualObjective}</strong> instead — open the campaign in the workspace with that objective set.</li>
            </ul>
            {campaign.apiId && (
              <a
                href={`https://www.facebook.com/adsmanager/manage/campaigns?act=&selected_campaign_ids=${campaign.apiId}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                Open this campaign in Meta Ads Manager <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      )}

      <Card padded>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-text-primary">Ad Set</h4>
          {isLocked && (
            <span className="flex items-center gap-1 text-xs text-warning">
              <Lock size={12} /> Locked
              <button type="button" onClick={() => unlockAdSet(activeAdSetId!)} className="ml-1 underline">Edit</button>
            </span>
          )}
        </div>

        <FormField label="Ad set name" required>
          <Input
            value={name}
            disabled={isLocked}
            onChange={e => { setName(e.target.value); sf('name', e.target.value); }}
            placeholder="e.g. UAE — 25–44 — Leads"
          />
        </FormField>

        {/* Optimization goal + Billing event — packed into a single row */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <FormField label="Optimization goal" required>
            <Select
              value={optGoal}
              disabled={isLocked}
              onChange={e => {
                const goal = e.target.value;
                setOptGoal(goal); sf('optimization_goal', goal); onOptimizationGoalChange?.(goal);
                // Keep the billing pairing valid — Meta validates the two
                // together and rejects stale combinations with a cryptic error.
                const valid = (billingEvents ?? billingEventsForGoal(goal)).map(b => b.code);
                if (!valid.includes(billing)) { setBilling('IMPRESSIONS'); sf('billing_event', 'IMPRESSIONS'); }
              }}
            >
              {optimizationGoals.map(g => {
                const costCapBlocked = campaignBidStrategy === 'COST_CAP' && COST_CAP_REFUSED_GOALS.includes(g.code);
                return (
                  <option key={g.code} value={g.code} disabled={costCapBlocked && g.code !== optGoal}>
                    {g.label}{costCapBlocked ? ' — not with Cost cap' : goalNotice(expectedObjective, g.code, noticeLocation) ? ' — needs setup' : ''}
                  </option>
                );
              })}
            </Select>
            {(() => {
              const notice = goalNotice(expectedObjective, optGoal, noticeLocation);
              if (!notice) return null;
              return (
                <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-warning/40 bg-warning-soft px-2.5 py-1.5">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
                  <span className="text-2xs leading-snug text-warning">{notice.text}</span>
                </div>
              );
            })()}
          </FormField>
          <FormField
            label="Billing event"
            required
            hintBelow
            hint={goalBillingEvents.length === 1 ? 'Meta only allows Impressions billing with this goal.' : undefined}
          >
            <Select
              value={billing}
              disabled={isLocked}
              onChange={e => { setBilling(e.target.value); sf('billing_event', e.target.value); }}
            >
              {goalBillingEvents.map(b => <option key={b.code} value={b.code}>{b.label}</option>)}
            </Select>
          </FormField>
        </div>

        {/* Bid cap — visible only when the campaign's bid strategy supports it.
            Hidden for LOWEST_COST_WITHOUT_CAP (Meta rejects bid_amount in that
            mode) so non-developers can't accidentally produce an invalid combo. */}
        {showBidField ? (
          <FormField label={bidLabel} hint={bidHint} hintBelow required>
            <Input
              type="number"
              value={bidAmt}
              disabled={isLocked}
              onChange={e => { setBidAmt(e.target.value); sf('bid_amount', e.target.value); }}
              placeholder="0.00"
            />
          </FormField>
        ) : (
          <div className="rounded-lg border border-border bg-surface-alt/40 px-3 py-2 text-2xs text-text-muted">
            <strong className="text-text-secondary">Automatic bidding</strong> — Meta finds the lowest
            cost results within your budget. Switch the campaign's bid strategy to "Bid cap", "Cost cap",
            or "Target cost" if you want to set a manual ceiling.
          </div>
        )}

        {/* Budget (only when ABO is set on the campaign) — either-or, segmented */}
        {showBudget && (
          <FormField label="Ad set budget">
            <div className="flex items-center gap-2">
              <Seg
                options={[{ v: 'daily', l: 'Daily' }, { v: 'lifetime', l: 'Lifetime' }]}
                value={budgetKind}
                disabled={isLocked}
                onChange={(v) => {
                  const k = v as 'daily' | 'lifetime';
                  setBudgetKind(k);
                  sf('budget_kind', k);
                }}
              />
              <Input
                type="number"
                value={budget}
                disabled={isLocked}
                onChange={e => {
                  setBudget(e.target.value);
                  if (budgetKind === 'daily') sf('daily_budget', e.target.value);
                  else sf('lifetime_budget', e.target.value);
                  sf('budget_kind', budgetKind);
                }}
                placeholder={budgetKind === 'daily' ? '50' : '1000'}
              />
              <span className="inline-flex items-center gap-1 text-xs font-medium text-text-muted">{currency ?? '—'} <CurrencyTip platform="meta" currency={currency} /></span>
            </div>
          </FormField>
        )}

        {/* Schedule — custom popover datetime pickers. End time is required
            when budgetKind is 'lifetime' (Meta enforces it). */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <FormField label="Start time" hint="Defaults to now if blank.">
            <DateTimePicker
              value={startTime}
              disabled={isLocked}
              onChange={v => { setStart(v); sf('start_time', v); }}
              placeholder="Start date & time"
            />
          </FormField>
          <FormField
            label="End time"
            required={needsEnd}
            hintBelow
            hint={
              campaignLifetime
                ? 'Required — the campaign spends a lifetime budget. Must be more than 24 hours after the start.'
                : needsEnd
                  ? 'Required for lifetime budget — Meta needs to know when to spend by.'
                  : 'Leave blank to run until you pause it.'
            }
          >
            <DateTimePicker
              value={endTime}
              disabled={isLocked}
              onChange={v => { setEnd(v); sf('end_time', v); }}
              placeholder="End date & time"
            />
          </FormField>
        </div>

        {/* Per-objective extra fields */}
        {children}
      </Card>

      {/* Targeting */}
      <Card padded>
        <h4 className="text-sm font-semibold text-text-primary mb-4">Audience targeting</h4>
        <TargetingSection
          value={targeting}
          disabled={isLocked}
          onChange={t => { setTargeting(t); sf('targeting', t); }}
        />
      </Card>

      {/* Placements */}
      <Card padded>
        <h4 className="text-sm font-semibold text-text-primary mb-1">Placements</h4>
        <p className="text-2xs text-text-muted mb-3">
          {platform === 'instagram'
            ? 'Pick which Instagram surfaces this ad set runs on. Leave all unchecked to use every IG placement.'
            : 'Where your ads will appear. Use Automatic to let Meta optimize, or pick specific platforms (e.g. Facebook only).'}
        </p>
        <PlacementPicker
          value={placement}
          disabled={isLocked}
          // IG workspace locks the picker to Instagram-only to prevent
          // accidental Facebook / Audience Network / Messenger placements.
          // See PlacementPicker — when locked it auto-forces manual mode
          // and `publisher_platforms = ['instagram']` on the saved payload.
          lockedPlatform={platform === 'instagram' ? 'instagram' : undefined}
          onChange={p => {
            setPlacement(p);
            sf('placement', p);
            onPlacementChange?.(p);
          }}
        />
      </Card>

      {/* Save */}
      {!isLocked && (
        <Button variant="primary" loading={isSubmitting} onClick={handleSave} disabled={!name}>
          {campaign.apiId ? 'Save Ad Set & Continue' : 'Save Campaign + Ad Set'}
        </Button>
      )}
      {isLocked && (
        <Button variant="primary" onClick={goToAd}>
          {activeAdSet?.ads.length ? 'Go to ad' : 'Create ad'}
        </Button>
      )}
      {!campaign.apiId && (
        <p className="text-xs text-text-muted">
          Tip — clicking save here will also create the campaign on Meta first.
        </p>
      )}
    </div>
  );
};
